export const heroChecks = [
  '5-Step Assessment Pathway',
  'Structured Wound Documentation Templates',
  'Red-Flag Recognition Checklists',
  'Communication & Escalation Decision Maps',
  'Follow-Up Tracking Tools',
  '6 Practical Volumes + 5 Actionable Bonuses',
];

export const problemSignals = ['Drainage', 'Surrounding tissue', 'Discomfort', 'Wound integrity'];

export const problemQuestions = [
  'What should you document?',
  'Which findings require additional assessment?',
  'What information should you communicate?',
  "When should you escalate according to your facility's protocols?",
  "How do you ensure the next clinician receives a clear picture of what's changed?",
];

export const pathwaySteps = [
  {
    title: 'Establish the Baseline',
    text: 'Organize relevant surgical history, existing orders, prior wound documentation, and current assessment context.',
  },
  {
    title: 'Assess & Record',
    text: 'Use structured templates to document observable wound characteristics and relevant patient-reported findings.',
  },
  {
    title: 'Identify Concerning Changes',
    text: 'Compare findings with previous assessments and organize potential warning signs requiring professional review.',
  },
  {
    title: 'Communicate & Escalate',
    text: 'Prepare concise clinical handoffs using structured communication tools and established escalation protocols.',
  },
  {
    title: 'Document & Follow Up',
    text: 'Record actions, communication, responses, and follow-up requirements to support continuity of care.',
  },
];

export const pathwayFlow = ['Observation', 'Documentation', 'Communication', 'Follow-Up'];

export const beforeItems = [
  'Wound observations recorded inconsistently.',
  'Important changes scattered across multiple notes.',
  'Uncertainty about which details belong in a handoff.',
  'Difficulty comparing findings across assessments.',
  'Escalation communication prepared under pressure.',
  'Follow-up information difficult to organize.',
];

export const afterItems = [
  'Consistent assessment organization.',
  'Standardized wound observation records.',
  'Clear comparison of documented changes.',
  'Structured identification of findings requiring review.',
  'Organized communication and escalation preparation.',
  'Practical continuity-of-care documentation.',
];

export const volumes = [
  {
    number: '01',
    title: 'Postoperative Wound Assessment Roadmap™',
    tagline: 'Build your assessment workflow from the first observation.',
    inside: [
      'Initial assessment sequence',
      'Surgical context review checklist',
      'Baseline observation worksheet',
      'Patient-reported findings organizer',
      'Assessment preparation checklist',
      'Five-step workflow reference sheet',
    ],
    deliverable: 'Initial Postoperative Wound Assessment Checklist',
  },
  {
    number: '02',
    title: 'Surgical Wound Documentation Blueprint™',
    tagline: 'Turn individual observations into structured clinical records.',
    inside: [
      'Wound location and characteristics template',
      'Incision appearance documentation matrix',
      'Drainage observation worksheet',
      'Surrounding tissue assessment fields',
      'Patient symptom documentation form',
      'Progress-note organization template',
    ],
    deliverable: 'Standardized Wound Documentation Form',
  },
  {
    number: '03',
    title: 'Postoperative Wound Red-Flag Decision Map™',
    tagline: 'Organize findings that may require further professional evaluation.',
    inside: [
      'Concerning wound changes checklist',
      'Possible infection-related findings organizer',
      'Wound separation observation matrix',
      'Drainage change documentation tool',
      'Patient condition change prompts',
      'Protocol-based escalation reference',
    ],
    deliverable: 'Wound Warning-Sign Recognition Matrix',
  },
  {
    number: '04',
    title: 'Wound Escalation & Communication Pathway™',
    tagline: 'Prepare clear, structured communication when concerning findings arise.',
    inside: [
      'SBAR communication framework',
      'Escalation preparation checklist',
      'Relevant clinical information organizer',
      'Provider notification documentation template',
      'Communication follow-up worksheet',
      'Handoff completeness checklist',
    ],
    deliverable: 'Postoperative Wound Escalation & SBAR Toolkit',
  },
  {
    number: '05',
    title: 'Wound Progress Monitoring Blueprint™',
    tagline: 'Compare observations and organize changes across assessments.',
    inside: [
      'Serial assessment comparison table',
      'Wound change tracking worksheet',
      'Drainage observation log',
      'Symptom documentation tracker',
      'Follow-up assessment checklist',
      'Clinical review preparation sheet',
    ],
    deliverable: 'Wound Progress Monitoring Dashboard',
  },
  {
    number: '06',
    title: 'Postoperative Wound Continuity-of-Care System™',
    tagline: 'Organize the information needed for ongoing monitoring and interdisciplinary coordination.',
    inside: [
      'Shift-to-shift handoff checklist',
      'Follow-up responsibility tracker',
      'Interdisciplinary communication organizer',
      'Documentation completion audit',
      'Transition-of-care information sheet',
      'Integrated workflow checklist',
    ],
    deliverable: 'Postoperative Wound Continuity-of-Care Pack',
  },
];

export const bonuses = [
  {
    number: '01',
    title: 'Postoperative Wound Assessment Quick Cards™',
    text: 'Compact assessment prompts for organizing relevant observations during professional monitoring.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/a1302485-1e40-438b-a0ee-4a06c435deac.jpg',
  },
  {
    number: '02',
    title: 'Surgical Wound Documentation Template Pack™',
    text: 'Reusable forms for recording wound characteristics, relevant changes, and communication details.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/ab224d8a-089b-47fa-996a-5e2b10b77a93.jpg',
  },
  {
    number: '03',
    title: 'Wound Red-Flag Reference Matrix™',
    text: 'A structured reference for organizing findings that may warrant prompt professional review under applicable protocols.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/60b24d8d-9924-4c03-853e-578b166c7b03.jpg',
  },
  {
    number: '04',
    title: 'SBAR Wound Communication Scripts™',
    text: 'Adaptable communication frameworks for presenting observations to the appropriate clinical team.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/aa245081-772e-452e-aa7b-b279d62a5ebe.jpg',
  },
  {
    number: '05',
    title: 'Postoperative Wound Follow-Up Tracker™',
    text: 'Practical worksheets for organizing reassessments, communication records, and outstanding follow-up items.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/9272e883-d1f1-43a0-a926-9b2fffd050fc.jpg',
  },
];

export const audiences = [
  { title: 'Surgical Nurses', text: 'Organize postoperative wound observations, documentation, and handoffs.' },
  { title: 'Postoperative Care Nurses', text: 'Use structured assessment and follow-up tools during postoperative monitoring.' },
  { title: 'Wound Care Professionals', text: 'Support consistent documentation and interdisciplinary communication.' },
  {
    title: 'Qualified Healthcare Practitioners',
    text: 'Integrate practical observation and escalation documentation tools into their professional workflows.',
  },
];

export const workflowSteps = [
  { title: 'Open Your Assessment Pathway', text: 'Start with the relevant checklist or documentation template.' },
  {
    title: 'Organize Your Observations',
    text: 'Record applicable wound characteristics, changes, and relevant patient information.',
  },
  {
    title: 'Prepare Your Communication',
    text: 'Use the structured handoff and escalation tools when indicated by clinical findings and local protocols.',
  },
  { title: 'Complete Your Documentation', text: 'Organize follow-up information and support continuity of care.' },
];

export const testimonials = [
  {
    name: 'Maria Flores, RN',
    role: 'Surgical Services Nurse',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/16305607-c0c0-411e-87e8-695230fdf1c9.jpg',
    quote:
      'Before this, my wound notes lived in three different places. The documentation form and the serial comparison table make it so easy to show exactly what changed between assessments. My handoffs are shorter and much clearer now.',
  },
  {
    name: 'Daniel Ortega, RN',
    role: 'Postoperative Care Nurse',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/1592e637-6945-4c14-9278-660d57cdea65.jpg',
    quote:
      "The SBAR scripts were the part I didn't know I needed. When I have to call a provider about a drainage change, my information is already organized in the right order. It takes so much pressure off.",
  },
  {
    name: 'Lucía Ramírez, BSN',
    role: 'Wound Care Nurse',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/1f9f2f81-ea65-44fa-996c-332daac0140e.jpg',
    quote:
      "Practical, no fluff. I adapted the templates to my facility's requirements in one afternoon, and the red-flag matrix now lives inside my binder. Worth far more than $27.",
  },
];

export const offerVolumes = [
  'Postoperative Wound Assessment Roadmap™',
  'Surgical Wound Documentation Blueprint™',
  'Postoperative Wound Red-Flag Decision Map™',
  'Wound Escalation & Communication Pathway™',
  'Wound Progress Monitoring Blueprint™',
  'Postoperative Wound Continuity-of-Care System™',
];

export const offerBonuses = [
  'Assessment Quick Cards™',
  'Documentation Template Pack™',
  'Red-Flag Reference Matrix™',
  'SBAR Communication Scripts™',
  'Follow-Up Tracker™',
];

export const offerTrust = [
  'Instant Digital Access',
  '11 Practical Resources',
  'Ready-to-Use Templates',
  '7-Day Money-Back Guarantee',
];

export const faqs = [
  {
    q: 'Is this designed for nursing professionals?',
    a: 'Yes. The blueprint is designed primarily for surgical nurses, postoperative care nurses, wound care professionals, and other qualified healthcare practitioners involved in postoperative monitoring.',
  },
  {
    q: 'Is this a traditional wound care textbook?',
    a: 'No. It focuses on operational tools, structured documentation, assessment checklists, communication frameworks, and practical workflows.',
  },
  {
    q: 'Does it provide treatment protocols?',
    a: 'No. It is a professional workflow-support resource. Clinical interventions must follow applicable institutional protocols, current professional guidance, authorized orders, and individual clinical judgment.',
  },
  {
    q: 'Can I use the templates in my clinical setting?',
    a: "The templates are designed as adaptable professional resources. Their use must comply with your organization's documentation requirements, approved procedures, and applicable privacy policies.",
  },
  {
    q: 'Does the blueprint replace clinical judgment?',
    a: 'No. It does not replace professional assessment, institutional escalation procedures, clinical supervision, or medical decision-making.',
  },
  {
    q: 'How will I receive the materials?',
    a: "You'll receive digital access to the included resources after your purchase.",
  },
  { q: 'Is this a recurring subscription?', a: 'No. The $27 purchase is a one-time payment.' },
  {
    q: "What if I'm not satisfied?",
    a: 'Your purchase includes a 7-day money-back guarantee, subject to the applicable refund policy.',
  },
];

export const recentBuyers = [
  { name: 'Valentina R.', country: 'Mexico' },
  { name: 'Carlos M.', country: 'Colombia' },
  { name: 'Lucía G.', country: 'Spain' },
  { name: 'Andrés P.', country: 'Argentina' },
  { name: 'Camila S.', country: 'Chile' },
  { name: 'Mateo F.', country: 'Peru' },
  { name: 'Sofía L.', country: 'Ecuador' },
  { name: 'Diego H.', country: 'Guatemala' },
  { name: 'Isabella T.', country: 'Costa Rica' },
  { name: 'Javier C.', country: 'Uruguay' },
  { name: 'Gabriela V.', country: 'Dominican Republic' },
  { name: 'Fernanda Q.', country: 'Bolivia' },
  { name: 'Ricardo A.', country: 'Panama' },
  { name: 'Daniela O.', country: 'Puerto Rico' },
  { name: 'Alejandro N.', country: 'Paraguay' },
];
